// Port of ios/Sources/Calls/CallsMsgPack.swift (Android `CallsMsgPack`, byte for byte).
//
// The slice of MessagePack the Calls plugin's wire format actually uses — written here rather than
// pulled in as a library because the surface is tiny and fixed: the SDP frame this client sends is
// one small map, and the data-channel messages it exchanges are a flat sequence of (int, payload)
// values.
//
// Why MessagePack exists on this path at all: the server reads an SDP out of a websocket message as
// raw bytes (`req.Data["data"].([]byte)` in the plugin), and JSON has no byte-array type — so the
// one message that carries compressed bytes must be a binary, MessagePack-encoded frame, while
// every other signaling message stays JSON text. Same story on the data channel.

export type MsgPackValue = number | string | boolean | null | Uint8Array | MsgPackValue[] | { [key: string]: MsgPackValue };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export class MsgPackWriter {
  private out: number[] = [];

  toBytes(): Uint8Array {
    return Uint8Array.from(this.out);
  }

  packMapHeader(size: number): this {
    if (size > 15) throw new Error('only fixmap is needed on this wire');
    this.out.push(0x80 | size);
    return this;
  }

  packString(value: string): this {
    const bytes = encoder.encode(value);
    const n = bytes.length;
    if (n <= 31) this.out.push(0xa0 | n);
    else if (n <= 0xff) this.out.push(0xd9, n);
    else if (n <= 0xffff) this.out.push(0xda, n >> 8, n & 0xff);
    else this.out.push(0xdb, ...int32(n));
    for (const b of bytes) this.out.push(b);
    return this;
  }

  packInt(value: number): this {
    if (value >= 0 && value <= 127) this.out.push(value);
    else if (value >= -32 && value <= -1) this.out.push(0xe0 | (value & 0x1f));
    else if (value >= 0 && value <= 0xff) this.out.push(0xcc, value);
    else if (value >= 0 && value <= 0xffff) this.out.push(0xcd, value >> 8, value & 0xff);
    else if (value >= 0 && value <= 0xffff_ffff) this.out.push(0xce, ...int32(value));
    else {
      const big = BigInt.asUintN(64, BigInt(value));
      this.out.push(value >= 0 ? 0xcf : 0xd3);
      for (let shift = 56n; shift >= 0n; shift -= 8n) this.out.push(Number((big >> shift) & 0xffn));
    }
    return this;
  }

  packBinary(value: Uint8Array): this {
    const n = value.length;
    if (n <= 0xff) this.out.push(0xc4, n);
    else if (n <= 0xffff) this.out.push(0xc5, n >> 8, n & 0xff);
    else this.out.push(0xc6, ...int32(n));
    for (const b of value) this.out.push(b);
    return this;
  }
}

function int32(value: number): number[] {
  return [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff];
}

export class MsgPackDecodeError extends Error {}

/**
 * Decodes the FLAT value sequence data-channel messages use: `encode(type) + encode(payload)`,
 * concatenated with no envelope (calls-common `dc_msg.js`). Returns every value in order; a
 * payload-less message (ping/pong) is a single value. Map keys on this wire are always strings; a
 * non-string key is skipped with its value. Throws `MsgPackDecodeError` on truncated/unknown input.
 */
export function decodeAll(data: Uint8Array): MsgPackValue[] {
  const reader = new Reader(data);
  const values: MsgPackValue[] = [];
  while (reader.hasNext) values.push(reader.readValue());
  return values;
}

class Reader {
  private pos = 0;
  constructor(private readonly data: Uint8Array) {}

  get hasNext(): boolean {
    return this.pos < this.data.length;
  }

  readValue(): MsgPackValue {
    const byte = this.next();
    if (byte < 0x80) return byte;
    if (byte >= 0xe0) return byte - 0x100;
    if (byte >= 0x80 && byte <= 0x8f) return this.readMap(byte & 0x0f);
    if (byte >= 0x90 && byte <= 0x9f) return this.readArray(byte & 0x0f);
    if (byte >= 0xa0 && byte <= 0xbf) return this.readString(byte & 0x1f);
    switch (byte) {
      case 0xc0:
        return null;
      case 0xc2:
        return false;
      case 0xc3:
        return true;
      case 0xc4:
        return this.readBytes(this.next());
      case 0xc5:
        return this.readBytes(this.readLength(2));
      case 0xc6:
        return this.readBytes(this.readLength(4));
      case 0xca:
        return new DataView(this.readBytes(4).slice().buffer).getFloat32(0);
      case 0xcb:
        return new DataView(this.readBytes(8).slice().buffer).getFloat64(0);
      case 0xcc:
        return this.next();
      case 0xcd:
        return this.readLength(2);
      case 0xce:
        return this.readLength(4);
      case 0xcf:
        return Number(this.readBig(8));
      case 0xd0: {
        const v = this.next();
        return v >= 0x80 ? v - 0x100 : v;
      }
      case 0xd1: {
        const v = this.readLength(2);
        return v >= 0x8000 ? v - 0x10000 : v;
      }
      case 0xd2:
        return this.readLength(4) | 0;
      case 0xd3:
        return Number(BigInt.asIntN(64, this.readBig(8)));
      case 0xd9:
        return this.readString(this.next());
      case 0xda:
        return this.readString(this.readLength(2));
      case 0xdb:
        return this.readString(this.readLength(4));
      case 0xde:
        return this.readMap(this.readLength(2));
      case 0xdc:
        return this.readArray(this.readLength(2));
      default:
        throw new MsgPackDecodeError(`unsupported 0x${byte.toString(16)}`);
    }
  }

  private next(): number {
    if (this.pos >= this.data.length) throw new MsgPackDecodeError('truncated');
    return this.data[this.pos++];
  }

  private readLength(bytes: number): number {
    let value = 0;
    for (let i = 0; i < bytes; i++) value = value * 256 + this.next();
    return value;
  }

  private readBig(bytes: number): bigint {
    let value = 0n;
    for (let i = 0; i < bytes; i++) value = (value << 8n) | BigInt(this.next());
    return value;
  }

  private readBytes(length: number): Uint8Array {
    if (this.pos + length > this.data.length) throw new MsgPackDecodeError('truncated');
    const out = this.data.slice(this.pos, this.pos + length);
    this.pos += length;
    return out;
  }

  private readString(length: number): string {
    return decoder.decode(this.readBytes(length));
  }

  private readMap(size: number): { [key: string]: MsgPackValue } {
    const map: { [key: string]: MsgPackValue } = {};
    for (let i = 0; i < size; i++) {
      const key = this.readValue();
      const value = this.readValue();
      if (typeof key === 'string') map[key] = value;
    }
    return map;
  }

  private readArray(size: number): MsgPackValue[] {
    const list: MsgPackValue[] = [];
    for (let i = 0; i < size; i++) list.push(this.readValue());
    return list;
  }
}
